import type { Editor } from '@tiptap/core';
import { history } from '@tiptap/pm/history';
import type { Node as PMNode } from '@tiptap/pm/model';

import { staticFunctions } from '@shared/contexts/ToastContext.context';
import setContentAndNotify from '../../helpers/setContentAndNotify.helper';
import type { THeadingState, TTreeHeading } from '../../tiptap/nodes/heading/headingNumbering';
import { IMAGE_NODE_NAME } from '../../tiptap/nodes/image/constants';
import { hasFailedEditorOperation, hasPendingEditorOperations } from '../../helpers/pendingEditorOperations.helper';
import { isImagePending } from '../../helpers/isImagePending.helper';
import { EDITOR_OPERATION_FAILED_MESSAGE } from '../../constants';
import type { TDocument, TDraft, TVersion } from '../../types';
import {
  anchorOffsetTop,
  correctAnchor,
  endHold,
  forgetScroller,
  holdAnchor,
  pauseBrowserScrollAdjust,
  rememberScroller,
  resumeBrowserScrollAdjust,
  viewportAnchor,
  type TAnchor,
} from './anchorHold';
import { aimCaretAt, clearPendingCaret, placeCaretAtPendingClick, type TPoint } from './caretRestore';
import { BUILDING_CLASS, type TSectionPiece } from '../constants';
import type { TElement } from '../model/elements';
import { createDocumentModel, type TDocumentModel, type TModelSnapshot } from '../model/documentModel';
import { installDebugApi } from '../debugApi';
import { shouldLoadInParts } from '../flags';
import { currentEditor, isLiveEditor, liveEditorDestroyed, setLiveEditor } from './liveEditor';
import type { TNumberingSeed } from '../model/numbering';
import { invalidateNumbering, renderStats } from '../static/staticRenderCache';
import { clearTrace, geometry, setMeasuring, setTraceScroller, trace, traceLog } from '../trace';
import { parseStats } from '../model/wholeDocument';

/** A swap whose content never arrives must not leave the editor hidden forever. */
const SWAP_FAILSAFE_MS = 8000;

const EMPTY_SNAPSHOT: TModelSnapshot = { elementsAbove: [], elementsBelow: [] };

let model: TDocumentModel | null = null;
let loadedHtml: string | null = null;
/** A copy of a document has byte-identical html, so html alone would hand one document's model to another. */
let loadedDocId: string | null = null;
let loadedVersionId: string | null = null;
let swapping = false;
let partSwap = false;
let swapToken = 0;
let swapStartedAt = 0;
/** The part whose content actually reached the editor - the failsafe can end a swap where it never did. */
let editorHoldsPart: number | null = null;
/** Set when a commit was refused, so the save path cannot upload a document missing the live part. */
let commitRefused = false;
/** The editor's doc as of the last commit. ProseMirror docs are immutable, so identity means untouched. */
let docAtLastCommit: PMNode | null = null;
/** The edit session, not `editor.isEditable`, which an explicit save turns off mid-edit. */
let requestEdit = false;

const listeners = new Set<() => void>();
/** Separate from `listeners`: swapping changes twice per swap, the element list not at all. */
const swapListeners = new Set<() => void>();
let unsubscribeModel: (() => void) | null = null;
const swapWaiters = new Set<() => void>();
let pendingContentSet: { editor: Editor; cancel: () => void } | null = null;

const cancelPendingContentSet = (editor?: Editor) => {
  if (!pendingContentSet || (editor && pendingContentSet.editor !== editor)) return;
  pendingContentSet.cancel();
  pendingContentSet = null;
};

/** True once a document is loaded through the model. Everything else keeps its editor-based path. */
export const isLargeDocument = (): boolean => model !== null;

export const isPartSwap = (): boolean => swapping && partSwap;

export const subscribeSwapState = (listener: () => void) => {
  swapListeners.add(listener);
  return () => {
    swapListeners.delete(listener);
  };
};

const setSwapping = (value: boolean, isPart = partSwap) => {
  if (swapping === value && partSwap === isPart) return;
  swapping = value;
  partSwap = isPart;
  // Every way a swap can end runs through here, including the cancelled ones.
  if (!value) resumeBrowserScrollAdjust();
  swapListeners.forEach((listener) => listener());
};

/** True while the editor is hidden and rebuilding, part swap or first load. */
export const isSwapping = (): boolean => swapping;

export const whenSwapDone = (callback: () => void) => {
  if (!swapping) {
    callback();
    return;
  }
  swapWaiters.add(callback);
};

export const subscribeElements = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getElementsSnapshot = (): TModelSnapshot => model?.getSnapshot() ?? EMPTY_SNAPSHOT;

/** Its identity changes on commit and on nothing else - the signal that the document's content changed. */
export const elementList = (): readonly TElement[] | null => model?.elements() ?? null;

/**
 * Registered by StaticElements. The static DOM must change in the SAME task the editor is hidden or
 * revealed, or the page is a part short for a few frames. React's render is a task too late.
 */
let syncStaticNow: (() => void) | null = null;
export const registerStaticSync = (sync: (() => void) | null) => {
  syncStaticNow = sync;
};

/**
 * Returns the committed document, or includes unsaved edits from `livePart` when the part is ready.
 * Reading never commits or serializes content.
 */
export const wholeDoc = (editor: Editor, { livePart }: { livePart?: PMNode } = {}): PMNode | null => {
  if (!model || editor !== currentEditor()) return null;
  // A recut can renumber the part before notifying readers; the model still owns the exact live
  // range. Only a swap or content that never arrived makes that range unsafe to overlay.
  const canIncludeLive = !swapping && editorHoldsPart !== null;
  return model.wholeDoc(canIncludeLive ? livePart : undefined);
};

/** Callers that are NOT saving must leave `clean` out - it strips markup an uncommitted comment selection needs. */
export const wholeDocumentHtml = (editor: Editor, clean?: (html: string) => string): string | null => {
  if (!model || editor !== currentEditor()) return null;
  commit();
  if (commitRefused)
    throw new Error('[largeDocument] refusing to save: the editor and the model hold different parts.');
  return model.saveHtml(clean);
};

/** The document's title, taken from element 0 - a part other than the first has no title node. */
export const documentTitle = (): string | null => model?.title() ?? null;

/** False during a swap: the editor's content is being replaced, so it belongs to neither part. */
export const holdsDocumentStart = (): boolean => !model || (!swapping && model.holdsStart());

export const holdsDocumentEnd = (): boolean => !model || (!swapping && model.holdsEnd());

/** Mid-swap the editor still holds the previous part, so every id in it would look like a duplicate of itself. */
export const idUsedEarlierInDocument = (editor: Editor, id: string): boolean =>
  model !== null &&
  editor === currentEditor() &&
  !swapping &&
  editorHoldsPart === model.activePart() &&
  model.hasIdBeforeActivePart(id);

/**
 * Take a comment's highlight out of the parts the editor does not hold.
 *
 * The editor removes it from its own part; on a large document that is one part out of a hundred, and
 * the rest kept a highlight with no comment behind it. Nothing is loaded to do this - a document
 * jumping to some other part because a comment was deleted would read as a bug of its own.
 */
export const removeCommentFromOtherParts = (commentId: string): boolean => {
  // Waits for a swap, because this is the one path that re-cuts the parts without going through a
  // commit: doing it mid-swap moves the range out from under the content the editor was handed, and
  // the reveal then draws that content twice and saves it over a part nothing is drawing.
  if (isSwapping()) {
    whenSwapDone(() => removeCommentFromOtherParts(commentId));
    return true;
  }
  if (!model?.removeCommentOutsideLivePart(commentId)) return false;
  // Unwrapping a comment above the live part re-cuts the parts, which can renumber the one the
  // editor holds. Its content did not change, so the index is all that has to catch up.
  editorHoldsPart = model.activePart();
  return true;
};

/** True when any element carries a landscape section, including elements the editor does not hold. */
export const hasSectionElement = (): boolean | null => model?.hasSectionNode() ?? null;

export const liveSectionPiece = (): TSectionPiece | null => model?.liveSectionPiece() ?? null;

/** Lets the editor's auto-index plugins carry on the document's numbering instead of restarting at 1. */
export const numberingSeed = (): TNumberingSeed | null => model?.numberingSeed() ?? null;

export const headingSeed = (headingStart: number): THeadingState | null => model?.headingSeed(headingStart) ?? null;

/** Pass `liveDoc` to include edits not committed yet; after a commit the model already has them. */
export const headingTree = (headingStart: number, liveDoc?: PMNode): TTreeHeading[] | null =>
  model?.headingTree(headingStart, liveDoc) ?? null;

const HEADING_TAG = /^<(h[1-6])\b/i;
/** A refused commit means the editor's work is not in the model - swapping would discard it silently. */
const commitBeforeSwap = (): boolean => {
  const editor = currentEditor();
  let hasPendingImage = false;
  // `file` is not rendered to HTML, and both mutations finish through their image node view. A swap
  // would unmount that view and commit an image which can no longer receive its final blob details.
  editor?.state.doc.descendants((node) => {
    if (node.type.name !== IMAGE_NODE_NAME) return;
    if (isImagePending(node.attrs, loadedDocId)) hasPendingImage = true;
  });
  if (hasPendingImage) {
    staticFunctions.show('warning', 'Please wait for the image upload to finish.');
    trace('swap:rejected', { reason: 'image upload or copy in flight' });
    return false;
  }
  if (editor && loadedDocId && hasPendingEditorOperations(editor, loadedDocId, requestEdit)) {
    if (hasFailedEditorOperation(editor)) staticFunctions.show('error', EDITOR_OPERATION_FAILED_MESSAGE);
    else staticFunctions.show('warning', 'Please wait for the inserted content to finish loading.');
    trace('swap:rejected', { reason: 'inserted content still loading' });
    return false;
  }
  commit();
  return !commitRefused;
};

const headingShape = (elements: readonly TElement[]): string =>
  elements.reduce((shape, element) => {
    const match = HEADING_TAG.exec(element.html);
    return match ? `${shape}${match[1]},` : shape;
  }, '');

const commit = () => {
  const editor = currentEditor();
  // Mid-swap the editor is still being rebuilt, so its html does not belong to any element range yet.
  if (!model || !editor || swapping) {
    trace('commit:skipped', { reason: swapping ? 'swapping' : 'no model or editor' });
    return;
  }
  // Only content that actually arrived may be written back.
  if (editorHoldsPart !== model.activePart()) {
    trace('commit:refused', { editorHoldsPart, activePart: model.activePart() });
    commitRefused = true;
    return;
  }
  commitRefused = false;
  // Untouched since the last commit - browsing, not editing. Serializing and re-parsing the whole
  // document to write back what is already there would make the click that asked for the swap wait.
  if (editor.state.doc === docAtLastCommit) return;
  const started = performance.now();
  const before = model.elements();
  const elementsBefore = before.length;
  const parsesBefore = parseStats.count;
  docAtLastCommit = editor.state.doc;
  const html = editor.getHTML();
  if (!model.commitFromEditor(html, editor.state.doc)) {
    trace('commit:unchanged', { ms: Math.round(performance.now() - started) });
    return;
  }
  const after = model.elements();
  // A commit re-cuts the parts and a cut can move, so a stale index refuses every later commit.
  editorHoldsPart = model.activePart();
  // Heading numbers are computed at runtime, never stored - a renumber leaves every element string the same.
  if (headingShape(before) !== headingShape(after)) invalidateNumbering();
  // A commit may only change the live part. Anything the editor did not give back is content the
  // document just lost, so list it instead of letting the count quietly drift.
  const lost =
    after.length < elementsBefore
      ? before
          .filter((element) => element.id && !after.some((kept) => kept.id === element.id))
          .slice(0, 6)
          // The html too: which two elements merged is the question, and the ids are gone from the
          // document by the time anyone reads this.
          .map((element) => ({ id: element.id, html: element.html.slice(0, 160) }))
      : undefined;
  trace('commit', {
    ms: Math.round(performance.now() - started),
    elementsBefore,
    elementsAfter: after.length,
    parts: model.parts().length,
    parses: parseStats.count - parsesBefore,
    lostElements: lost,
  });
  // Losing content is the one thing here that must not be quiet. The trace is for digging in later;
  // this is so nobody has to be watching for it.
  if (lost?.length) {
    console.error(
      // `lost.length`, not the net change: creating a reusable block out of four elements replaces
      // them with one, so the net says 3 while four ids are listed - which reads as a miscount in
      // the one message that has to be trusted.
      `[largeDocument] the editor did not return ${lost.length} element(s) of the part it was given; ` +
        'they are no longer in the document. Ids: ' +
        lost.map((element) => element.id).join(', ')
    );
  }
};

/** `offsetTop` is where the element ACTUALLY is: a target near the end cannot reach the requested offset. */
export const holdScrollTarget = (scroller: HTMLElement, elementId: string) => {
  if (!model) return; // an ordinary document does not move after a scroll
  rememberScroller(scroller);
  setTraceScroller(scroller);
  const offsetTop = anchorOffsetTop(scroller, elementId);
  if (offsetTop === null) return;
  holdAnchor({ scroller, elementId, offsetTop, scrollTop: scroller.scrollTop }, hasElementId);
};

const setEditorBuilding = (editor: Editor, building: boolean) => {
  (editor.view.dom as HTMLElement).classList.toggle(BUILDING_CLASS, building);
};

const finishSwap = (token: number, target: number, anchor: TAnchor | null, done?: () => void) => {
  if (token !== swapToken || !swapping) {
    trace('swap:finish-ignored', { token, swapToken, swapping });
    return;
  }
  const editor = currentEditor();
  const current = model;
  if (!editor || !current) return;

  // Hand the live part back to the editor. In one task with the DOM sync and the reveal below, so the
  // page is never a part short or a part long. `active` already points at `target` - see `swapTo`.
  trace('geo:before-reveal', geometry());
  current.setActivePart(target);
  current.drawAroundLivePart();
  syncStaticNow?.();
  trace('geo:after-reveal-sync', geometry());
  setEditorBuilding(editor, false);
  trace('geo:after-editor-shown', geometry());
  // Once, after the last change - see `swapTo`.
  if (anchor) correctAnchor(anchor);

  setSwapping(false, false);

  // One top-level node per element. When the parser merges or drops one, the next commit splices the
  // shorter list in and the difference leaves the document - so name it rather than let it drift.
  const range = current.parts()[target];
  // `range` can be missing if the model was replaced while this swap was in flight. Everything below
  // is a diagnostic; throwing here would skip the caret, the swap waiters and `done` - and `done` is
  // what makes the editor editable after "Edit draft", so the document would open uneditable.
  const liveNodes = range ? current.liveElementNodes(editor.state.doc) : null;
  if (range && liveNodes && range.endElement - range.startElement !== liveNodes.length) {
    const expectedElements = range.endElement - range.startElement;
    const given = current
      .elements()
      .slice(range.startElement, range.endElement)
      .map((element) => element.id);
    const returned = new Set<string | null>(liveNodes.map((node) => node.attrs?.id ?? null));
    trace('swap:not-round-tripped', {
      part: target,
      expectedElements,
      editorNodes: liveNodes.length,
      missingIds: given.filter((id) => id && !returned.has(id)).slice(0, 10),
    });
  }

  const snapshot = current.getSnapshot();
  trace('swap:finished', {
    part: target,
    ms: Math.round(performance.now() - swapStartedAt),
    liveElements: liveNodes?.length ?? current.liveElementNodes(editor.state.doc).length,
    elementsAbove: snapshot.elementsAbove.length,
    elementsBelow: snapshot.elementsBelow.length,
    hasAnchor: Boolean(anchor),
  });

  placeCaretAtPendingClick();

  const waiters = [...swapWaiters];
  swapWaiters.clear();
  waiters.forEach((callback) => callback());
  done?.();
};

const swapTo = (target: number, isPart: boolean, anchor: TAnchor | null, done?: () => void) => {
  const editor = currentEditor();
  const current = model;
  if (!editor || !current) return;

  // One setContent in flight at a time: it defers the mutation and only checks staleness afterwards,
  // so an overlapping swap can land its content after the newer one finished.
  if (swapping) {
    trace('swap:queued', { to: target, behind: swapToken });
    swapWaiters.add(() => swapTo(target, isPart, anchor, done));
    return;
  }

  // Leaving edit mode reloads the same document, and rebuilding it flashes the static copy in its place.
  if (!isPart && current.partHtml(target) === editor.getHTML()) {
    current.setActivePart(target);
    current.drawAroundLivePart();
    editorHoldsPart = target;
    docAtLastCommit = editor.state.doc;
    // Held, not corrected once: the static halves still re-place, images still resolve, and the
    // measuring pass still runs on an idle frame - all after this returns. The full path holds for
    // 2.5s for exactly that reason, and skipping the rebuild does not skip the settling.
    if (anchor) holdAnchor(anchor);
    syncStaticNow?.();
    if (anchor) correctAnchor(anchor);
    trace('swap:unchanged', { part: target });
    done?.();
    return;
  }

  const token = ++swapToken;
  setSwapping(true, isPart);
  // Paired with the resume in `setSwapping`, so the browser is stood down for exactly as long as the
  // swap runs - not from part-way through it.
  pauseBrowserScrollAdjust();
  // The target may have the same numeric index after a re-cut. Only content arrival proves that the
  // editor holds its new range; a failed or timed-out setContent must leave commits refused.
  editorHoldsPart = null;
  swapStartedAt = performance.now();
  trace('swap:start', {
    from: current.activePart(),
    to: target,
    isPartSwap: isPart,
    partHtmlChars: current.partHtml(target).length,
    hasAnchor: Boolean(anchor),
  });

  // The static halves draw the outgoing part too while the editor rebuilds hidden, so the page keeps
  // its height throughout.
  trace('geo:before-static-draws-all', geometry());
  current.drawWholeDocumentStatically(target);
  trace('geo:after-static-draws-all', geometry());
  /**
   * Point at the target BEFORE its content goes in. Everything derived while `setContent` runs - the
   * heading tree, the numbering seeds - asks which part is live, and pointing at the source folds the
   * new content into the source's element range. Safe because commits are blocked for the whole swap.
   */
  current.setActivePart(target);
  // Corrected once, after the LAST change in this task - a correction nobody can see still forces a layout.
  if (anchor) holdAnchor(anchor);
  syncStaticNow?.();
  trace('geo:after-static-sync', geometry());
  setEditorBuilding(editor, true);
  trace('geo:after-editor-hidden', geometry());
  if (anchor) correctAnchor(anchor);

  const failSafe = window.setTimeout(() => {
    // A superseded swap must not cancel the one that replaced it: `cancelPendingContentSet` matches
    // on the editor, which a newer swap shares.
    if (token !== swapToken) return;
    cancelPendingContentSet(editor);
    finishSwap(token, target, anchor, done);
  }, SWAP_FAILSAFE_MS);

  cancelPendingContentSet();
  const cancelContentSet = setContentAndNotify(
    editor,
    current.partHtml(target),
    () => {
      if (pendingContentSet?.cancel === cancelContentSet) pendingContentSet = null;
      trace('swap:content-set', {
        to: target,
        ms: Math.round(performance.now() - swapStartedAt),
        stale: token !== swapToken,
      });
      // Our own timer, cleared on every exit: a superseded swap that leaves it running fires eight
      // seconds later and cancels whatever swap owns the editor by then.
      window.clearTimeout(failSafe);
      if (token !== swapToken) return; // a newer swap owns the editor now
      // setContentAndNotify already waited for the React node views to mount and their images to load,
      // which is what would otherwise pop into place after the reveal.
      finishSwap(token, target, anchor, done);
    },
    undefined,
    (phase, ms, applied) => {
      trace(`swap:${phase}`, { to: target, ms: Math.round(ms) });
      // `applied` is false when a `filterTransaction` blocked the content - the editor still holds
      // the old part, so recording the new one here would make the next commit save the wrong range.
      if (phase === 'parse' && token === swapToken) {
        if (applied === false) {
          trace('swap:content-rejected', { to: target });
        } else {
          editorHoldsPart = target;
          docAtLastCommit = editor.state.doc;
          /**
           * Cleared in the SAME step that records the new part, never later.
           *
           * Undoing the swap's own `setContent` would put the previous part's content into this
           * part's element range, and the next commit would save it there. Node views and images
           * settle after this, so a reset that waits for them leaves Undo armed in between - and a
           * swap whose completion is lost never resets it at all.
           */
          editor.unregisterPlugin('history');
          editor.registerPlugin(history());
        }
      }
      // Put the anchor back in the same task as the height change - the hold runs a frame later.
      if (anchor) correctAnchor(anchor);
    }
  );
  pendingContentSet = { editor, cancel: cancelContentSet };
};

const forget = () => {
  cancelPendingContentSet();
  // Whatever hid the editor for a swap, this is the end of that swap. Left set, the class keeps the
  // editor `visibility: hidden; position: absolute` and the document area is blank until a later swap
  // happens to clear it - turning fast loading off mid-load is enough to get there.
  const hidden = currentEditor();
  if (hidden) setEditorBuilding(hidden, false);
  unsubscribeModel?.();
  unsubscribeModel = null;
  model?.dispose();
  model = null;
  setLiveEditor(null);
  loadedHtml = null;
  loadedDocId = null;
  loadedVersionId = null;
  commitRefused = false;
  setSwapping(false, false);
  editorHoldsPart = null;
  docAtLastCommit = null;
  swapToken++;
  swapWaiters.clear();
  endHold();
  forgetScroller();
  clearPendingCaret();
  clearTrace();
  listeners.forEach((listener) => listener());
};

export const setRequestEdit = (value: boolean) => {
  requestEdit = value;
};

/** Release only the controller state owned by `editor`; a newer editor must never be torn down by an old cleanup. */
export const releaseDocument = (editor: Editor) => {
  if (isLiveEditor(editor)) {
    forget();
    return;
  }
  cancelPendingContentSet(editor);
};

type TLoadOptions = {
  editor: Editor;
  html: string;
  /** Decides whether this document loads in parts at all - see `isPartLoadable`. */
  doc: TDocument;
  version: TDraft | TVersion | null | undefined;
  onLoaded?: () => void;
};

/** Replaces `setContentAndNotify` at every call site that pushes a whole document into the editor. */
export const loadDocument = ({ editor, html, doc, version, onLoaded }: TLoadOptions) => {
  // React destroys an editor after its screen unmounts. Module state lives longer, so browser Back
  // can arrive while `editorRef` still names that dead instance. Nothing may read its view or schema.
  if (liveEditorDestroyed()) forget();

  if (!shouldLoadInParts(doc, html)) {
    // Forget the previous document first, or the save path answers for it and uploads its html here.
    forget();
    const cancelContentSet = setContentAndNotify(
      editor,
      html,
      () => {
        if (pendingContentSet?.cancel === cancelContentSet) pendingContentSet = null;
        onLoaded?.();
      },
      undefined,
      undefined,
      true
    );
    pendingContentSet = { editor, cancel: cancelContentSet };
    return;
  }

  const versionId = version && 'id' in version ? version.id : 'draft';
  const sameContent = model !== null && html === loadedHtml && doc._id === loadedDocId && versionId === loadedVersionId;
  const sameEditor = isLiveEditor(editor);
  // The same blob delivered into the same editor again (a refetch on window focus) must not throw
  // away which part the user is working in.
  if (sameContent && sameEditor) {
    trace('load:same-content', { chars: html.length });
    onLoaded?.();
    return;
  }

  // A new editor instance for the same document (React re-created it) is about to take over. Fold
  // the outgoing one's content in first, or every edit since the last commit is dropped.
  /**
   * Leaving edit mode, or any reload after the content changed, rebuilds the model - and a new model
   * points at part 0, so the editor loads the beginning of the document and the page has nowhere to
   * be but the top. Taken before the rebuild, while the old DOM is still on the page.
   */
  const keepPlace = loadedDocId === doc._id ? viewportAnchor() : null;
  if (sameContent && !sameEditor) commit();

  setLiveEditor(editor);
  if (!sameContent) {
    // `finishSwap` checks only its token, so a stale swap would point the NEW model at the OLD one's part index.
    if (swapping) {
      trace('swap:cancelled', { reason: 'model replaced' });
      cancelPendingContentSet();
      swapToken++;
      swapWaiters.clear();
      editorHoldsPart = null;
      setEditorBuilding(editor, false);
      setSwapping(false, false);
    }
    const started = performance.now();
    unsubscribeModel?.();
    model?.dispose();
    clearTrace();
    model = createDocumentModel({ schema: editor.schema, html });
    loadedHtml = html;
    loadedDocId = doc._id;
    loadedVersionId = versionId;
    unsubscribeModel = model.subscribe(() => listeners.forEach((listener) => listener()));
    listeners.forEach((listener) => listener());
    trace('load', {
      documentId: doc._id,
      chars: html.length,
      elements: model.elements().length,
      parts: model.parts().length,
      ms: Math.round(performance.now() - started),
    });
  } else {
    trace('load:new-editor', { chars: html.length });
  }
  // The edit may have moved that element into a different part, so ask the new model where it is now.
  const held = keepPlace ? model!.partOfId(keepPlace.elementId) : -1;
  swapTo(held >= 0 ? held : model!.activePart(), false, keepPlace, onLoaded);
};

/** The part index is resolved AFTER the commit: committing shifts every index below the live part. */
export const activateElementId = (
  elementId: string,
  options?: {
    clickPoint?: TPoint;
    /** The far end of a dragged selection, so it survives the swap - see `caretRestore`. */
    selectionEnd?: TPoint;
    anchor?: { scroller: HTMLElement; offsetTop: number };
    /**
     * Where the clicked element sits. Exact, unlike `elementId`, which walks back to the nearest
     * element that has one - a comment mark has none, so that walk names an earlier part.
     */
    elementIndex?: number;
    /**
     * The innermost thing under the click. The caret aims relative to THIS, because a paragraph deep
     * inside a table moves on its own when the table lays out differently.
     */
    aimId?: string | null;
  }
): boolean => {
  if (!model || !currentEditor() || swapping) {
    trace('activate:rejected', { elementId, reason: swapping ? 'swap in flight' : 'no model or editor' });
    return false;
  }
  if (options?.anchor) {
    rememberScroller(options.anchor.scroller);
    setTraceScroller(options.anchor.scroller);
  }
  const fromPart = model.activePart();
  /**
   * The element the click landed on, held as the element itself rather than as its position.
   *
   * The commit below can add or remove elements - typing a new paragraph or pasting a table does -
   * and everything after the edit then shifts. A position read from the page before that points at
   * the element's NEIGHBOUR afterwards, so the swap loads the part next to the one clicked and the
   * clicked one is still not in the editor. The element survives the commit: only the live part's
   * elements are rebuilt, and the clicked one is outside it or the click would already be live.
   */
  const clickedElement = options?.elementIndex === undefined ? null : (model.elements()[options.elementIndex] ?? null);
  // Membership of the live part cannot change by committing the live part itself, so this answers
  // "already live?" before the commit and saves a re-parse when the answer is yes.
  const partOfClick = (): number => {
    if (clickedElement) {
      const index = model!.elements().indexOf(clickedElement);
      if (index >= 0) return model!.partOfElementIndex(index);
    }
    return model!.partOfId(elementId);
  };
  const clickIsLive = (): boolean => {
    const index = clickedElement ? model!.elements().indexOf(clickedElement) : -1;
    return model!.isElementInActiveRange(elementId, index >= 0 ? index : undefined);
  };
  const partBeforeCommit = partOfClick();
  trace('activate:requested', {
    elementId,
    fromPart,
    partBeforeCommit,
    clickPoint: options?.clickPoint,
    anchorOffsetTop: options?.anchor ? Math.round(options.anchor.offsetTop) : undefined,
  });
  if (clickIsLive()) {
    trace('activate:rejected', { elementId, reason: 'already live', part: fromPart });
    return false;
  }

  if (!commitBeforeSwap()) {
    trace('activate:rejected', { elementId, reason: 'commit refused - the editor holds another part' });
    return false;
  }
  const target = partOfClick();
  if (target < 0 || clickIsLive()) {
    trace('activate:rejected', {
      elementId,
      reason: target < 0 ? 'target not in document' : 'already live after commit',
      target,
    });
    return false;
  }
  if (target !== partBeforeCommit) trace('activate:part-shifted', { elementId, partBeforeCommit, target });

  aimCaretAt(options?.clickPoint ?? null, options?.selectionEnd ?? null, options?.aimId ?? elementId);
  const anchor = options?.anchor
    ? { ...options.anchor, elementId, scrollTop: options.anchor.scroller.scrollTop }
    : null;
  swapTo(target, true, anchor);
  return true;
};

const hasElementId = (elementId: string): boolean => (model ? model.partOfId(elementId) >= 0 : false);

installDebugApi({
  active: isLargeDocument,
  parts: () => model?.parts().length ?? 0,
  activePart: () => model?.activePart() ?? -1,
  elements: () => model?.elements().length ?? 0,
  liveElements: () => {
    const editor = currentEditor();
    return editor && model ? model.liveElementNodes(editor.state.doc).length : 0;
  },
  docSize: () => currentEditor()?.state.doc.content.size ?? 0,
  swapping: isSwapping,
  parses: () => parseStats,
  /** Per-part sizes: chars, elements, images, node views, table cells. */
  shape: () => model?.partShape() ?? null,
  renders: () => renderStats,
  log: traceLog,
  clearLog: clearTrace,
  measure: setMeasuring,
  goToId: activateElementId,
  goToPart: (index: number) => {
    if (!model || !currentEditor() || swapping) return false;
    const target = Math.max(0, Math.min(index, model.parts().length - 1));
    if (target === model.activePart()) return false;
    if (!commitBeforeSwap()) return false;
    swapTo(target, true, null);
    return true;
  },
});
